/**
 * ELK (Eclipse Layout Kernel) layered layout via elkjs.
 * Async — returns a Promise. Suitable for worker offloading later.
 */

import ELK from "elkjs/lib/elk.bundled.js";
import {
  KG_DATA_STORY_NODE_WIDTH_PX,
  KG_REPORT_NODE_HEIGHT_PX,
  KG_REPORT_NODE_HEIGHT_WITH_FOOTER_PX,
  KG_REPORT_NODE_WIDTH_PX,
  KG_SQUARE_NODE_SIZE_PX,
} from "../../kgNodeDimensions";
import {
  NODE_WIDTH,
  NODE_HEIGHT,
  TIER_PARTITION,
  buildElkOptions,
  type ElkDirection,
} from "./elkConfig";
import type { KgFlowEdge, KgFlowNode } from "../types";

const resolveNodeDimensions = (
  node: KgFlowNode,
  defaults: { width: number; height: number },
): { width: number; height: number } => {
  if (node.type === "dataSource") {
    return {
      width: KG_SQUARE_NODE_SIZE_PX,
      height: KG_SQUARE_NODE_SIZE_PX,
    };
  }

  if (node.type === "report") {
    const hasFooter = Boolean(node.data?.biName || node.data?.biLogoUrl);
    return {
      width: node.measured?.width ?? KG_REPORT_NODE_WIDTH_PX,
      height:
        node.measured?.height ??
        node.height ??
        (hasFooter
          ? KG_REPORT_NODE_HEIGHT_WITH_FOOTER_PX
          : KG_REPORT_NODE_HEIGHT_PX),
    };
  }

  if (node.type === "dataStory") {
    return {
      width: node.measured?.width ?? KG_DATA_STORY_NODE_WIDTH_PX,
      height: node.measured?.height ?? node.height ?? defaults.height,
    };
  }

  return {
    width: node.measured?.width ?? node.width ?? defaults.width,
    height: node.measured?.height ?? node.height ?? defaults.height,
  };
};

const elk = new ELK();

export interface GetElkLayoutOptions {
  direction?: ElkDirection;
  nodeWidth?: number;
  nodeHeight?: number;
  layerSpacing?: number;
  nodeSpacing?: number;
  wrap?: boolean;
  aspectRatio?: number;
}

export interface ElkLayoutResult {
  nodes: KgFlowNode[];
  edges: KgFlowEdge[];
}

export const getElkLayout = async (
  nodes: KgFlowNode[],
  edges: KgFlowEdge[],
  options: GetElkLayoutOptions = {},
): Promise<ElkLayoutResult> => {
  const {
    direction = "LR",
    nodeWidth = NODE_WIDTH,
    nodeHeight = NODE_HEIGHT,
    layerSpacing = 5,
    nodeSpacing = 80,
    wrap = false,
    aspectRatio = 1.6,
  } = options;

  const layoutOptions = buildElkOptions({
    direction,
    layerSpacing,
    nodeSpacing,
    wrap,
    aspectRatio,
  });

  const graph = {
    id: "root",
    layoutOptions,
    children: nodes.map((n) => {
      const partition =
        typeof n.data?.depth === "number"
          ? n.data.depth
          : n.type
            ? TIER_PARTITION[n.type]
            : undefined;
      const { width, height } = resolveNodeDimensions(n, {
        width: nodeWidth,
        height: nodeHeight,
      });
      const child: {
        id: string;
        width: number;
        height: number;
        layoutOptions?: Record<string, string>;
      } = { id: n.id, width, height };
      if (partition !== undefined) {
        child.layoutOptions = {
          "elk.partitioning.partition": String(partition),
        };
      }
      return child;
    }),
    edges: edges.map((e) => ({
      id: e.id,
      sources: [e.source],
      targets: [e.target],
    })),
  };

  // elkjs types are loose; cast for clarity.
  const result = (await elk.layout(graph as never)) as {
    children?: { id: string; x?: number; y?: number }[];
  };
  const posById = new Map(
    (result.children ?? []).map((c) => [c.id, { x: c.x ?? 0, y: c.y ?? 0 }]),
  );

  const layoutedNodes: KgFlowNode[] = nodes.map((node) => {
    const pos = posById.get(node.id) ?? { x: 0, y: 0 };
    return {
      ...node,
      position: { x: pos.x, y: pos.y },
      sourcePosition: direction === "LR" ? "right" : "bottom",
      targetPosition: direction === "LR" ? "left" : "top",
    } as KgFlowNode;
  });

  return { nodes: layoutedNodes, edges };
};
